<script setup lang="ts">
// 1:1 port of `customer-apps/api-image/editable-image`. Angular uses it in exactly one place
// (news-form.component.html:53) — the profile image and the gallery are DIFFERENT components
// (styleguide `kgr-profile-image` resp. `gallery-image-upload`), so they deliberately stay where
// they are. Cropping and its load-failed state live in `KgrImageCropper`, as in Angular where the
// component embeds `image-cropper`; this component owns display, placeholder and the edit flyout.
import KgrAuthImage from './KgrAuthImage.vue';
import KgrIcon from './KgrIcon.vue';
import { ref } from 'vue';

withDefaults(
    defineProps<{
        /** Already-resolved preview URL, or null for the "Kein Bild" placeholder. */
        imageUrl?: string | null
        roundedCorners?: boolean
        aspectRatio?: number
    }>(),
    { imageUrl: null, roundedCorners: true },
);

const emit = defineEmits<{ select: [event: Event], requestDelete: [] }>();

const flyoutOpen = ref(false);
</script>

<template>
    <div class="editableImage">
        <label
            v-if="!imageUrl"
            class="placeholder kgr-image kgr-clickable kgr-clickable--touchy"
            :class="{ 'kgr-image--rounded': roundedCorners }"
            :style="{ aspectRatio }"
        >
            <input
                type="file"
                accept="image/*"
                class="kgr-input is-hidden"
                @change="emit('select', $event)"
            />
            <div class="editButton kgr-profileImage__editButton kgr-clickable kgr-clickable--touchy">
                <KgrIcon name="edit" />
            </div>
            {{ $t('Kein Bild') }}
        </label>
        <template v-else>
            <KgrAuthImage
                :src="imageUrl"
                alt=""
                class="kgr-image"
                :class="{ 'kgr-image--rounded': roundedCorners }"
                style="width: 100%"
            />
            <div
                class="editButton kgr-profileImage__editButton kgr-clickable kgr-clickable--touchy"
                :class="{ 'is-flyoutOpen': flyoutOpen }"
                @click.stop="flyoutOpen = !flyoutOpen"
            >
                <KgrIcon name="edit" />
                <div class="kgr-profileImage__editButtonFlyout">
                    <label
                        class="kgr-profileImage__editButtonFlyoutButton kgr-clickable kgr-clickable--touchy"
                        :title="$t('Bearbeiten')"
                    >
                        <input
                            type="file"
                            accept="image/*"
                            class="kgr-input is-hidden"
                            @change="emit('select', $event)"
                        />
                        <KgrIcon name="edit" />
                    </label>
                    <button
                        type="button"
                        class="kgr-profileImage__editButtonFlyoutButton kgr-clickable kgr-clickable--touchy"
                        :title="$t('Löschen')"
                        @click="
                            flyoutOpen = false;
                            emit('requestDelete');
                        "
                    >
                        <KgrIcon name="delete" />
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
/* editable-image.component.scss: `:host { display: inline-block; position: relative }`. The width
   comes from the call site (Angular passes `style="width: 190px"`). */
.editableImage {
    position: relative;
    display: inline-block;
}

.editButton {
    position: absolute;
    top: 5px;
    right: 5px;
    z-index: 1;

    &.is-flyoutOpen :deep(.kgr-profileImage__editButtonFlyout) {
        transform: scale(1);
        pointer-events: all;
    }
}

.placeholder {
    padding: 60px;
    border: 1px solid var(--kgr-color-neutral);
    background-color: var(--kgr-color-neutral-lighter);
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
